import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createApiClient } from '@/lib/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('vos_token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // Debounce the session-expired toast so a page that fires 5 parallel
  // requests after expiry doesn't spam 5 identical toasts. CRITICALLY,
  // this ref must NOT be reset by setToken(null) during the auth-fail
  // flow itself — only on actual login (null → string). Otherwise a
  // burst of parallel 401s races between handleAuthFail invocations
  // and the toast leaks (testing-agent RCA iteration 63).
  const authFailFiredRef = useRef(false);
  // Track whether the boot-time /auth/me check has finished. Snapshot
  // this at the TOP of handleAuthFail so concurrent 401s don't see
  // mutated state mid-flight.
  const bootCompletedRef = useRef(false);
  // Previous-token ref so the reset-on-login useEffect can detect
  // null → string transitions without re-arming on string → null
  // (which is what happens during auth-fail itself).
  const prevTokenRef = useRef(token);

  const handleAuthFail = useCallback(() => {
    // Snapshot the boot flag at entry — see RCA in iteration 63: if we
    // read the ref later, a sibling microtask may have already flipped
    // it from false to true, causing the SECOND 401 to take the
    // user-action branch and leak a toast at boot.
    const wasBoot = !bootCompletedRef.current;
    if (authFailFiredRef.current) return;
    authFailFiredRef.current = true;
    if (wasBoot) {
      // Boot-time auth failure (stored token already expired before
      // any user action): clear silently — the AuthContext initial
      // render hasn't painted a UI the user could trust yet.
      localStorage.removeItem('vos_token');
      setToken(null);
      setUser(null);
      return;
    }
    // ── User-action auth failure (token expired DURING use) ──
    // Order matters (testing-agent root-cause Jun 13 2026):
    //   1. Show toast FIRST (synchronous trigger — Sonner queues it
    //      internally so it survives the SPA navigation that follows).
    //   2. Compute ?next= and SPA-navigate via React Router BEFORE
    //      clearing the token. If we cleared token first, the
    //      ProtectedRoute would re-render and `<Navigate to="/login"/>`
    //      would fire before our navigate(), stripping the ?next=.
    //   3. THEN clear token + user.
    toast.error('Session expired', {
      description: 'Please log in again — no data was changed.',
      duration: 6000,
    });
    const path = window.location.pathname || '';
    const search = window.location.search || '';
    if (!path.startsWith('/login')) {
      const next = encodeURIComponent(path + search);
      navigate(`/login?next=${next}`, { replace: true });
    }
    localStorage.removeItem('vos_token');
    setToken(null);
    setUser(null);
  }, [navigate]);

  // Reset the debounce ONLY when a fresh login arrives (null → string).
  // setToken(null) during auth-fail must NOT re-arm — that was the race
  // testing-agent caught in iteration 63 where parallel 401s leaked an
  // extra "Session expired" toast at boot.
  useEffect(() => {
    if (prevTokenRef.current === null && token !== null) {
      authFailFiredRef.current = false;
    }
    prevTokenRef.current = token;
  }, [token]);

  // Memoise the axios instance per-token so React doesn't rebuild it
  // on every render. Critical: useEffect deps that include `api` need
  // a stable reference or they'll loop.
  const api = useMemo(
    () => createApiClient({ token, onAuthFail: handleAuthFail }),
    [token, handleAuthFail],
  );

  useEffect(() => {
    if (token) {
      api.get('/auth/me')
        .then(res => {
          setUser(res.data);
          setLoading(false);
          bootCompletedRef.current = true;
        })
        .catch(() => {
          // Bad / expired token at boot — handleAuthFail (called by
          // the response interceptor) will silent-clear because
          // bootCompletedRef.current is still false.
          setLoading(false);
          bootCompletedRef.current = true;
        });
    } else {
      setLoading(false);
      bootCompletedRef.current = true;
    }
  }, [token, api]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('vos_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    authFailFiredRef.current = false;
    bootCompletedRef.current = true;
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('vos_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
